import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton } from "../components/Brutal";
import { Scales, CurrencyDollar, TrendUp, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MarginSettlementsPage() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await api.get("/margin/settlements", { params: { status } });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  useEffect(() => {
    api.get("/margin/weekly-summary").then(({ data }) => setWeekly(data.rows || [])).catch(() => {});
  }, []);

  const settle = async (row) => {
    if (!window.confirm(t("margin.settle_confirm", { name: row.distributor_name }))) return;
    setSettling(row.distributor_id);
    try {
      const res = await api.post(`/margin/settlements/${row.distributor_id}/settle`);
      toast.success(t("margin.settled_toast", { n: res.data.settled_count, name: row.distributor_name }));
      load(tab);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettling(null);
    }
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
      month: "short", day: "numeric",
    }) : "—";
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-8" data-testid="margin-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-3">
          <Scales size={30} weight="bold" className="text-[#c9a24a]" />
          {t("margin.page_title")}
        </h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">{t("margin.page_subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-sm" data-testid="margin-total-received">
          <div className="flex items-center gap-2 text-zinc-500 text-sm font-medium">
            <CurrencyDollar size={16} weight="bold" /> {t("margin.total_received")}
          </div>
          <p className="text-3xl sm:text-4xl font-semibold mt-2">{money(summary.total_received)}</p>
        </div>
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-sm" data-testid="margin-parent-revenue">
          <div className="flex items-center gap-2 text-zinc-500 text-sm font-medium">
            <TrendUp size={16} weight="bold" /> {t("margin.parent_revenue")}
          </div>
          <p className="text-3xl sm:text-4xl font-semibold mt-2">{money(summary.parent_revenue)}</p>
        </div>
        <div className="bg-[#fbf6e7] border-2 border-[#c9a24a] rounded-xl p-5 shadow-sm" data-testid="margin-spread-owed">
          <div className="flex items-center gap-2 text-[#8a6a1c] text-sm font-semibold">
            <Scales size={16} weight="bold" /> {t("margin.spread_owed")}
          </div>
          <p className="text-3xl sm:text-4xl font-semibold mt-2 text-[#8a6a1c]">{money(summary.spread_owed)}</p>
        </div>
      </div>

      {weekly.length > 0 && (
        <section className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="weekly-spread-summary">
          <h2 className="text-lg font-semibold tracking-tight">{t("margin.weekly_title")}</h2>
          <p className="text-xs text-zinc-500 mb-3">{t("margin.weekly_subtitle")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {weekly.map((w) => (
              <div
                key={w.parent_id}
                className="border border-zinc-200 rounded-lg p-4"
                data-testid={`weekly-spread-${w.parent_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold truncate">{w.parent_name}</span>
                  {w.parent_code && (
                    <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded">
                      {w.parent_code}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-semibold text-[#8a6a1c] mt-1 tabular-nums">{money(w.spread_week)}</p>
                <p className="text-[11px] text-zinc-500">
                  {t("margin.week_this")} · {t("margin.week_sales", { n: w.sales_week })}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-zinc-600 tabular-nums">
                  <span>{t("margin.week_prev")}: <b>{money(w.spread_prev_week)}</b></span>
                  <span>{t("margin.week_pending")}: <b className="text-amber-700">{money(w.pending_spread)}</b></span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex gap-2">
        {["pending", "settled"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            data-testid={`margin-tab-${s}`}
            className={`px-4 h-9 rounded-full border-2 text-sm font-semibold transition-colors ${
              tab === s
                ? "bg-[#c9a24a] border-[#c9a24a] text-zinc-950"
                : "bg-white border-zinc-200 text-zinc-700 hover:border-[#c9a24a]"
            }`}
          >
            {t(`margin.tab_${s}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-5 text-sm text-zinc-600">
          {t(tab === "pending" ? "margin.empty_pending" : "margin.empty_settled")}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.distributor_id}
              className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5 shadow-sm"
              data-testid={`margin-row-${r.distributor_id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-base truncate">{r.distributor_name}</p>
                    {r.reconciliation_code && (
                      <span className="text-[11px] font-bold px-1.5 py-0.5 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded font-mono tracking-wider">
                        {r.reconciliation_code}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {r.parent_name && `${t("margin.under", { name: r.parent_name })} · `}
                    {r.sale_count} {t("margin.sales")} · {fmtDate(r.period_start)} – {fmtDate(r.period_end)}
                    {r.settled_at && ` · ${t("margin.settled_on", { date: fmtDate(r.settled_at) })}`}
                  </p>
                </div>
                <div className="flex items-center gap-5 sm:gap-8">
                  <div className="text-right">
                    <p className="text-[11px] uppercase font-semibold text-zinc-400">{t("margin.col_total_sales")}</p>
                    <p className="font-semibold">{money(r.total_sales)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase font-semibold text-zinc-400">{t("margin.col_base_owed")}</p>
                    <p className="font-semibold">{money(r.base_wholesale_owed)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase font-semibold text-[#8a6a1c]">{t("margin.col_spread_owed")}</p>
                    <p className="font-bold text-[#8a6a1c]">{money(r.spread_owed)}</p>
                  </div>
                  {tab === "pending" ? (
                    <BrutalButton
                      size="sm"
                      variant="eliteFilled"
                      onClick={() => settle(r)}
                      disabled={settling === r.distributor_id}
                      data-testid={`settle-btn-${r.distributor_id}`}
                    >
                      <CheckCircle size={15} weight="bold" />
                      {settling === r.distributor_id ? t("margin.settling") : t("margin.mark_settled")}
                    </BrutalButton>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold">
                      <CheckCircle size={16} weight="fill" /> {t("margin.status_settled")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
